import {useEffect, useState} from 'react';

import {Skeleton} from '@mui/lab';
import {DialogContent} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {useSearchParams} from 'react-router-dom';

import useAuth from '../../../../hooks/providers/useAuth';
import useProfile from '../../../../hooks/providers/useProfile';
import BaseModal from '../../../modal/BaseModal';
import OnboardingSelectFlowCard from '../OnboardingFlowCard/OnboardingSelectFlowCard';
import OnboardingSingleFlowPage from '../OnboardingSingleFlowPage/OnboardingSingleFlowPage';

const OnboardingWelcomeModal = () => {
  const {
    selectedFlow,
    setSelectedFlow,
    setCurrentFlowStepId,
    onboardingData,
    profile,
    isOnboardingModalOpen: isOpen,
    setIsOnboardingModalOpen: setIsOpen,
    currentFlowStep,
    saveOnboardingVideoProgression
  } = useProfile();
  const [isFlowSelectionPage, setIsFlowSelectionPage] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // This hook aims to automatically redirects user to last not fully-seen video in current flow
  // Except if user has clicked on a specific video in onboarding button (then we'll have a QS param)
  useEffect(() => {
    if (!selectedFlow) return;

    const videoId = searchParams.get('videoId');
    if (selectedFlow && videoId) return;

    const unfinishedSteps = selectedFlow?.steps.filter(s => s.position !== 100);
    if (unfinishedSteps?.length > 0) {
      setCurrentFlowStepId(unfinishedSteps[0].step_id);
    }
  }, [selectedFlow?.id]);

  const onClose = () => {
    if (!isFlowSelectionPage) {
      saveOnboardingVideoProgression(currentFlowStep.step_id, currentFlowStep.position);
    }
    setIsOpen(false);
  };

  const handleFlowSelection = flow => {
    setIsFlowSelectionPage(false);
    setSelectedFlow(flow);
    setSearchParams([]);
  };

  const handleBackIconClick = () => {
    setIsFlowSelectionPage(true);
    setSelectedFlow(null);
    saveOnboardingVideoProgression(currentFlowStep.step_id, currentFlowStep.position);
  };

  const getModalTitle = () => {
    if (isFlowSelectionPage) {
      return <>Bienvenue {profile?.givenName ? profile.givenName : <Skeleton variant="text" sx={{fontSize: 14, display: 'inline-block', width: 50, lineHeight: 2}} />} ! On démarre ?</>;
    }
    return `${selectedFlow?.title}`;
  };
  const title = getModalTitle();

  useEffect(() => {
    if (selectedFlow?.id) {
      handleFlowSelection(selectedFlow);
    }
  }, [selectedFlow?.id]);

  const welcomeFlows = onboardingData.filter(f => f.show_on_welcome === true);

  const renderFlowCards = () => {
    if (isFlowSelectionPage) {
      return (
        <Grid container spacing={1.5} justifyContent="center">
          {welcomeFlows.map((flow, i) => {
            return (
              <Grid item xs={12} sm={6} md={4} onClick={() => handleFlowSelection(flow)} key={i}>
                <OnboardingSelectFlowCard flow={flow} />
              </Grid>
            );
          })}
        </Grid>
      );
    }
    return null;
  };

  const welcomeVideoUrl = onboardingData[0]?.welcome_video;
  const shouldDisplayBackIcon = !isFlowSelectionPage && onboardingData.some(f => f.show_on_welcome === true);

  const numberOfFlows = welcomeFlows.length;
  const videoColumnsWidthOnDesktop = numberOfFlows * 4;

  return (
    <BaseModal
      overridesPaperProps={{height: 'auto', maxHeight: 800}}
      hasBackIcon={shouldDisplayBackIcon}
      onBackIconClick={handleBackIconClick}
      maxWidth="lg"
      hasTitleDivider={false}
      hasCloseIcon
      onClose={onClose}
      open={isOpen}
      title={title}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 70px)',
          minHeight: 0, // allow shrinking
          pt: 1,
          pb: 2.5,
          px: {
            xs: 1,
            sm: 2.5
          }
        }}
      >
        {isFlowSelectionPage ? (
          <Grid container justifyContent="center" mt={1.5}>
            <Grid item xs={12} md={videoColumnsWidthOnDesktop} sx={{maxHeight: '45vh'}}>
              <Box
                style={{
                  aspectRatio: '15 / 9',
                  width: '100%',
                  maxHeight: '100%',
                  margin: '0 auto',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <Box
                  autoPlay
                  muted
                  data-testid="video"
                  component="video"
                  playsInline
                  controls
                  sx={{
                    width: 'auto',
                    maxWidth: '100%',
                    height: '100%',
                    margin: '0 auto !important',
                    display: 'block',
                    borderRadius: 2
                  }}
                  src={welcomeVideoUrl}
                />
              </Box>
            </Grid>
          </Grid>
        ) : null}

        <Box sx={{minHeight: 0, mt: isFlowSelectionPage ? 2 : 0}}>{renderFlowCards()}</Box>
        {selectedFlow ? <OnboardingSingleFlowPage selectedFlow={selectedFlow} /> : null}
      </DialogContent>
    </BaseModal>
  );
};

export default OnboardingWelcomeModal;
